import {
  ArrowForwardOutlined,
  CheckCircleOutlined,
  EmailOutlined,
  PhoneOutlined,
  TaskAltOutlined,
} from '@mui/icons-material';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';

const MotionBox = motion.create(Box);

const pilotScopes = ['项目周报与资料整理', '会议结论与行动项', '审批、日程与待办跟进'];

interface DownloadProps {
  onContact: () => void;
}

const Download: React.FC<DownloadProps> = ({ onContact }) => {
  const reduceMotion = useReducedMotion();

  return (
    <Box
      id="download"
      component="section"
      sx={{
        py: { xs: 9, md: 12 },
        backgroundColor: 'rgb(var(--surface-soft-rgb))',
      }}
    >
      <Container maxWidth="lg">
        <MotionBox
          {...(reduceMotion
            ? {}
            : {
                initial: { opacity: 1, y: 22 },
                whileInView: { opacity: 1, y: 0 },
                transition: { duration: 0.52 },
                viewport: { once: true, amount: 0.3 },
              })}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(340px, .9fr)' },
            gap: { xs: 4, lg: 7 },
            alignItems: 'center',
            p: { xs: 3, sm: 5, md: 6 },
            borderRadius: '32px',
            backgroundColor: 'rgb(var(--ink-rgb))',
            color: 'rgb(var(--surface-rgb))',
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              width: 340,
              height: 340,
              borderRadius: '50%',
              right: -150,
              top: -170,
              border: '58px solid rgba(var(--brand-rgb), .12)',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              sx={{
                color: 'rgb(var(--brand-rgb))',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.14em',
                mb: 1.5,
              }}
            >
              START WITH A REAL WORKFLOW
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3.2rem' },
                fontWeight: 800,
                letterSpacing: '-0.045em',
                lineHeight: 1.12,
              }}
            >
              先从一个真实办公场景开始。
            </Typography>
            <Typography sx={{ mt: 2, maxWidth: 620, color: 'rgba(250,249,244,.65)', lineHeight: 1.85 }}>
              飞舟团队会结合企业组织、项目资料和现有办公流程，梳理适合 Agent 接手的任务，
              从可验证的小范围场景逐步落地。
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3.5 }}>
              <Button
                onClick={onContact}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardOutlined />}
                sx={{
                  minHeight: 50,
                  px: 3.2,
                  borderRadius: '999px',
                  backgroundColor: 'rgb(var(--brand-rgb))',
                  color: 'rgb(var(--on-brand-rgb))',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: 'rgb(var(--brand-tint-rgb))', boxShadow: 'none' },
                }}
              >
                联系产品团队
              </Button>
              <Button
                component="a"
                href="tel:075523606709"
                size="large"
                startIcon={<PhoneOutlined />}
                sx={{
                  minHeight: 50,
                  px: 2.6,
                  borderRadius: '999px',
                  color: 'rgb(var(--surface-rgb))',
                  border: '1px solid rgba(255,255,255,.2)',
                  '&:hover': {
                    borderColor: 'rgba(var(--brand-rgb), .7)',
                    backgroundColor: 'rgba(var(--brand-rgb), .08)',
                  },
                }}
              >
                0755-23606709
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              p: { xs: 2.2, md: 2.6 },
              borderRadius: '22px',
              backgroundColor: 'rgba(255,255,255,.07)',
              border: '1px solid rgba(255,255,255,.10)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TaskAltOutlined sx={{ color: 'rgb(var(--accent-rgb))' }} />
              <Typography sx={{ fontWeight: 700 }}>适合优先试点的场景</Typography>
            </Box>
            <Stack spacing={1.2}>
              {pilotScopes.map((scope) => (
                <Box key={scope} sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                  <CheckCircleOutlined sx={{ color: 'rgb(var(--brand-rgb))', fontSize: 19 }} />
                  <Typography sx={{ color: 'rgba(250,249,244,.75)', fontSize: '0.88rem' }}>
                    {scope}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Box
              sx={{
                mt: 2.3,
                pt: 2,
                borderTop: '1px solid rgba(255,255,255,.10)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'rgba(250,249,244,.52)',
                fontSize: '0.75rem',
              }}
            >
              <EmailOutlined sx={{ fontSize: 17 }} />
              sales@qdtg.com
            </Box>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Download;
